// context/PaymentContext.jsx

import React, { createContext, useContext, useCallback } from "react";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PaymentContext = createContext(undefined);

export const PaymentProvider = ({ children }) => {
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("superadmintoken");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }, []);

  // Generic error handler for API responses
  const handleResponse = useCallback((response) => {
    // Check if response data has success: false
    if (response.data && response.data.success === false) {
      const errorMessage = response.data.error || response.data.message || "An error occurred";
      throw new Error(errorMessage);
    }
    return response.data;
  }, []);

  // ── Get all payout files ──────────────────────────────────────────────────
  const getAllPayoutFiles = useCallback(async ({ company_id, startDate, endDate, page, limit, file_status,payout_type ,role}) => {
    const response = await axios.post(
      `${BASE_URL}/payment/get-all-files`,
      { company_id, startDate, endDate, page, limit, file_status,payout_type,role },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Create payout file ────────────────────────────────────────────────────
  const createPayoutFile = useCallback(async ({ company_id, file_status = "file_generated" }) => {
    const response = await axios.post(
      `${BASE_URL}/payment/create-payout-file`,
      { company_id, file_status },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Submit / approve / reject payout file ────────────────────────────────
  const submitPayoutFile = useCallback(async ({
    payoutfiles_id,
    file_status,
    rejection_reason_by_admin,
    rejection_reason_by_accounts,
  }) => {
    const payload = {
      payoutfiles_id,
      file_status,
      ...(rejection_reason_by_admin && { rejection_reason_by_admin }),
      ...(rejection_reason_by_accounts && { rejection_reason_by_accounts }),
    };
    const response = await axios.post(
      `${BASE_URL}/payment/submit-payout-file`,
      payload,
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Payout file edit history ──────────────────────────────────────────────
  const getPayoutFileHistory = useCallback(async (payoutfiles_id) => {
    const response = await axios.post(
      `${BASE_URL}/payment/payoutfile-history`,
      { payoutfiles_id },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  
    const getPayoutAccountHistory = useCallback(async (payoutfiles_id) => {
    const response = await axios.post(
      `${BASE_URL}/payment/download-payout-csv`,
      { payoutfiles_id,
        onlyView:true
       },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

      const getPayoutreferralAccountHistory = useCallback(async (payoutfiles_id) => {
    const response = await axios.post(
      `${BASE_URL}/payment/download-referral-payout-csv`,
      { payoutfiles_id,
        onlyView:true
       },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);


        const getPayoutincentiveAccountHistory = useCallback(async (payoutfiles_id) => {
    const response = await axios.post(
      `${BASE_URL}/payment/download-incentive-payout-csv`,
      { payoutfiles_id,
        onlyView:true
       },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Download payout CSV ───────────────────────────────────────────────────
  // ── Download payout CSV ───────────────────────────────────────────────────
  
const downloadPayoutCSV = useCallback(async (payoutfiles_id, fileName, file_status) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/payment/download-payout-csv`,
      { payoutfiles_id, file_status },
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );

    if (response.data?.type === "application/json") {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        if (json.success === false) {
          throw new Error(json.error || json.message || "Download failed");
        }
      } catch (parseError) {
        throw new Error(parseError);
      }
    }

    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("text/csv") || contentType.includes("text/plain")) {
      throw new Error("Server sent a CSV file. Expected Excel format.");
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // ✅ Try content-disposition first, fallback to fileName param
    const contentDisposition = response.headers["content-disposition"];
    const match = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = match
      ? match[1].trim()
      : fileName.endsWith(".xlsx")
        ? fileName
        : `${fileName}.xlsx`;   // ✅ ensure .xlsx extension

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    throw error;
  }
}, [getAuthHeaders]);

const downloadreferralPayoutCSV = useCallback(async (payoutfiles_id, fileName, file_status) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/payment/download-referral-payout-csv`,
      { payoutfiles_id, file_status },
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );

    if (response.data?.type === "application/json") {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        if (json.success === false) {
          throw new Error(json.error || json.message || "Download failed");
        }
      } catch (parseError) {
        throw new Error(parseError);
      }
    }

    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("text/csv") || contentType.includes("text/plain")) {
      throw new Error("Server sent a CSV file. Expected Excel format.");
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // ✅ Try content-disposition first, fallback to fileName param
    const contentDisposition = response.headers["content-disposition"];
    const match = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = match
      ? match[1].trim()
      : fileName.endsWith(".xlsx")
        ? fileName
        : `${fileName}.xlsx`;

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    throw error;
  }
}, [getAuthHeaders]);


const downloadincentivePayoutCSV = useCallback(async (payoutfiles_id, fileName, file_status) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/payment/download-incentive-payout-csv`,
      { payoutfiles_id, file_status },
      {
        headers: getAuthHeaders(),
        responseType: "blob",
      }
    );

    if (response.data?.type === "application/json") {
      const text = await response.data.text();
      try {
        const json = JSON.parse(text);
        if (json.success === false) {
          throw new Error(json.error || json.message || "Download failed");
        }
      } catch (parseError) {
        throw new Error(parseError);
      }
    }

    const contentType = response.headers["content-type"] || "";
    if (contentType.includes("text/csv") || contentType.includes("text/plain")) {
      throw new Error("Server sent a CSV file. Expected Excel format.");
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // ✅ Try content-disposition first, fallback to fileName param
    const contentDisposition = response.headers["content-disposition"];
    const match = contentDisposition?.match(/filename="?([^"]+)"?/);
    const filename = match
      ? match[1].trim()
      : fileName.endsWith(".xlsx")
        ? fileName
        : `${fileName}.xlsx`;

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);

    return true;
  } catch (error) {
    throw error;
  }
}, [getAuthHeaders]);



  // ── List / view a single payout file (department-wise data) ──────────────
  const listPayoutFile = useCallback(async ({ payoutfiles_id, file_status = "file_generated",payout_type,companyId }) => {
    const response = await axios.post(
      `${BASE_URL}/payment/list-payout-file`,
      { file_status, payoutfiles_id,payout_type, companyId },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Regenerate payout file ────────────────────────────────────────────────
  const regeneratePayoutFile = useCallback(async ({ payoutfiles_id, lastFileStatus }) => {
    const response = await axios.post(
      `${BASE_URL}/payment/regenerate-payout-file`,
      { payoutfiles_id, lastFileStatus },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Combine / merge multiple payout files ────────────────────────────────
  const combinePayoutFiles = useCallback(async ({ payoutfiles_ids, file_status }) => {
    const response = await axios.post(
      `${BASE_URL}/payment/combine-payout-files`,
      { payoutfiles_ids, file_status },
      { headers: getAuthHeaders() }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);



    const uploadBankResponse = useCallback(async ({ payoutfiles_id, file }) => {
    const token = localStorage.getItem("superadmintoken");
    const formData = new FormData();
    formData.append("payoutFilesId", payoutfiles_id);
    formData.append("file", file);
    const response = await axios.post(
      `${BASE_URL}/payment/upload-bank-response`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

    const uploadreferralandincentiveBankResponse = useCallback(async ({ payoutfiles_id, file }) => {
    const token = localStorage.getItem("superadmintoken");
    const formData = new FormData();
    formData.append("payoutFilesId", payoutfiles_id);
    formData.append("file", file);
    const response = await axios.post(
      `${BASE_URL}/payment/upload-incentive-referral-bank-response`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return handleResponse(response);
  }, [getAuthHeaders, handleResponse]);

  // ── Edit worker listing data ──────────────────────────────────────────────
  const editWorkerListingData = useCallback(async (record) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/lenskart-admin/worker-data`,
        { 
          attendanceId: record.attendanceId, 
          workerId: record.userId 
        },
        { headers: getAuthHeaders() }
      );
      return handleResponse(response);
    } catch (error) {
      console.log("Error editing worker listing data:", error);
      throw error; 
    }
  }, [getAuthHeaders, handleResponse]);

  const value = {
    getAllPayoutFiles,
    createPayoutFile,
    submitPayoutFile,
    getPayoutFileHistory,
    getPayoutAccountHistory,
    getPayoutreferralAccountHistory,
    getPayoutincentiveAccountHistory,
    downloadPayoutCSV,
    downloadreferralPayoutCSV,
    downloadincentivePayoutCSV,
    listPayoutFile,
    regeneratePayoutFile,
    combinePayoutFiles,
    uploadBankResponse,
    uploadreferralandincentiveBankResponse, 
    editWorkerListingData, 
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};

export const usePayment = () => {
  const ctx = useContext(PaymentContext);
  if (!ctx) throw new Error("usePayment must be used inside <PaymentProvider>");
  return ctx;
};











const PAYOUT_MODE_ORDER = ["Daily_Payout", "Scheduled_Payout", "Upcoming_Scheduled_Payout"];